import { useMemo, useRef } from 'react'
import GeoJSON from 'geojson'
import { MapMouseEvent } from 'mapbox-gl'

import { ADHP } from '@/domain/models'
import { FuelData, FuelLayer } from '@/domain/protocols/Fuel'

import { MapSymbolLayer } from '../MapView/MapSymbolLayer'
import { MapSymbolSource } from '../MapView/MapSymbolSource'
import { icaoLayout, noPriceLayout, priceTextPaint, textPaint, withPriceLayout } from './MapFuel.styles'

import avgasLabel from '@/presentation/assets/fuel/avgasLabel.png'
import avgasLabelPrice from '@/presentation/assets/fuel/avgasLabelPrice.png'
import jetLabel from '@/presentation/assets/fuel/jetLabel.png'
import jetLabelPrice from '@/presentation/assets/fuel/jetLabelPrice.png'
import { formatNumberFixed } from '@/utils/number'

interface FuelAerodromeSummary {
  adhpCode: string
  providers: FuelData[]
}

interface MapFuelProps {
  summaries: FuelAerodromeSummary[]
  adhps: ADHP[]
  activeLayer: FuelLayer
  beforeLayerId?: string
  onLabelClick: (adhpCode: string) => void
}

function MapFuel({ summaries, adhps, activeLayer, beforeLayerId, onLabelClick }: MapFuelProps) {
  const lastHandledClickRef = useRef<MouseEvent | null>(null)

  const { withPriceFeatures, noPriceFeatures } = useMemo(() => {
    const withPrice: GeoJSON.Feature<GeoJSON.Point>[] = []
    const noPrice: GeoJSON.Feature<GeoJSON.Point>[] = []
    const adhpByCode = new Map(adhps.map((adhp) => [adhp.code, adhp]))

    for (const summary of summaries) {
      const adhp = adhpByCode.get(summary.adhpCode)
      if (!adhp) continue

      const layerProviders = summary.providers.filter((provider) =>
        activeLayer === 'avgas' ? Boolean(provider.avgasProvider) : Boolean(provider.jetProvider)
      )

      if (layerProviders.length === 0) continue

      const layerPrices = layerProviders
        .map((provider) => (activeLayer === 'avgas' ? provider.avgasPrice : provider.jetPrice))
        .filter((price): price is number => price != null)

      const lowestPrice = layerPrices.length > 0 ? Math.min(...layerPrices) : undefined

      const feature: GeoJSON.Feature<GeoJSON.Point> = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: adhp.coordinates.toArray()
        },
        properties: {
          adhpCode: adhp.code,
          code: adhp.code,
          priceLabel: lowestPrice != null ? `R$ ${formatNumberFixed(lowestPrice, 2)}` : ''
        }
      }

      if (lowestPrice != null) {
        withPrice.push(feature)
      } else {
        noPrice.push(feature)
      }
    }

    return {
      withPriceFeatures: { type: 'FeatureCollection' as const, features: withPrice },
      noPriceFeatures: { type: 'FeatureCollection' as const, features: noPrice }
    }
  }, [summaries, adhps, activeLayer])

  const handleClick = (event: MapMouseEvent) => {
    const adhpCode = event.features?.[0]?.properties?.adhpCode
    if (!adhpCode) return

    // Este handler está registrado nas 4 camadas, e o ícone e o rótulo ICAO de um mesmo ponto
    // compartilham a fonte e a coordenada. Um único clique físico atinge as duas camadas e chama
    // o handler duas vezes, com o mesmo `originalEvent` — só a primeira chamada deve valer.
    if (event.originalEvent && lastHandledClickRef.current === event.originalEvent) return
    lastHandledClickRef.current = event.originalEvent ?? null

    onLabelClick(adhpCode)
  }

  const imageWithPrice = activeLayer === 'avgas' ? avgasLabelPrice : jetLabelPrice
  const imageNoPrice = activeLayer === 'avgas' ? avgasLabel : jetLabel

  return (
    <>
      <MapSymbolSource id="fuel-with-price-source" data={withPriceFeatures}>
        <MapSymbolLayer
          id="fuel-with-price-layer"
          sourceId="fuel-with-price-source"
          imageSrc={imageWithPrice}
          layout={withPriceLayout}
          paint={priceTextPaint}
          beforeLayerId={beforeLayerId}
          minZoomLevel={6}
          onSymbolClick={handleClick}
        />
        <MapSymbolLayer
          id="fuel-with-price-icao-layer"
          sourceId="fuel-with-price-source"
          imageSrc={imageWithPrice}
          layout={icaoLayout}
          paint={textPaint}
          beforeLayerId={beforeLayerId}
          minZoomLevel={6}
          onSymbolClick={handleClick}
        />
      </MapSymbolSource>

      <MapSymbolSource id="fuel-no-price-source" data={noPriceFeatures}>
        <MapSymbolLayer
          id="fuel-no-price-layer"
          sourceId="fuel-no-price-source"
          imageSrc={imageNoPrice}
          layout={noPriceLayout}
          paint={textPaint}
          beforeLayerId={beforeLayerId}
          minZoomLevel={6}
          onSymbolClick={handleClick}
        />
        <MapSymbolLayer
          id="fuel-no-price-icao-layer"
          sourceId="fuel-no-price-source"
          imageSrc={imageNoPrice}
          layout={icaoLayout}
          paint={textPaint}
          beforeLayerId={beforeLayerId}
          minZoomLevel={6}
          onSymbolClick={handleClick}
        />
      </MapSymbolSource>
    </>
  )
}

export { MapFuel }
